/**
 * Today on the phone.
 *
 * One card that both times and logs, because away from a desk you have one
 * hand and about four seconds. Idle it is the everyday path — what it was, the
 * project, a preset length and Log, with ▶ beside the description to time it
 * instead. Task, kind, activity and date sit under More (open or closed is
 * remembered on this device), named on its row when any is set.
 * Running, it becomes a large clock with Stop & log and Discard; tapping the
 * project line opens the running sheet to change anything else.
 *
 * Each logged entry has ▶ to continue it, and swipes: right to continue,
 * left to delete (with undo).
 *
 * The day's total lives in the shell header rather than being repeated here.
 */

import { useEffect, useMemo, useState } from "react";

import { itemProjectId } from "@/components/ItemRow";
import { CalendarCard } from "@/components/CalendarCard";
import { card } from "@/components/atoms";
import { PlayIcon } from "@/components/NavIcons";
import { formatElapsed, useTimer } from "@/context/TimerContext";
import { KindPicker } from "@/components/KindPicker";
import { KIND_LABEL, defaultKind, fitKind, kindChoices, kindFields, type EntryKind } from "@/lib/billing";
import { useChrome } from "@/context/ChromeContext";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { effectiveRate, useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { groupTasksByPhase } from "@/lib/taskOptions";
import { useActivities } from "@/lib/theme";
import {
  clock, dayLabel, deriveEntryLabel, durationLabel, entryDayKey, hours,
  breakdownText, entryKind, inBounds, parseDuration, periodBounds, toIsoLocal, totalsOf,
} from "@/lib/timeRollup";
import type { TimeEntry } from "@/lib/types";

import { SwipeActions } from "@/components/SwipeRow";
import { useContinueEntry } from "@/hooks/useContinueEntry";
import { useUndoableDelete } from "@/hooks/useUndoableDelete";

import { PickerRow, SectionLabel, TaskPicker, field, screen, tapGhost, tapPrimary } from "../components";
import { useLogSheet } from "../components/LogSheetContext";

/** The amounts actually reached for, in the tracker's order. */
const PRESETS = [10, 15, 30, 60];
const MORE_KEY = "ph.mobile.timeMore";
const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

export function MobileToday() {
  const timer = useTimer();
  const toast = useToast();
  const logSheet = useLogSheet();
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { logEntry } = useTimeMutations();
  const [activities] = useActivities();

  const [projectId, setProjectId] = useState("");
  const [itemId, setItemId] = useState("");
  const [activity, setActivity] = useState("");
  const [note, setNote] = useState("");
  const [day, setDay] = useState(() => toIsoLocal(new Date()));
  const [minutes, setMinutes] = useState(30);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [kindPick, setKindPick] = useState<EntryKind | null>(null);
  const [more, setMore] = useState(() => {
    try { return localStorage.getItem(MORE_KEY) === "1"; } catch { return false; }
  });
  const continueEntry = useContinueEntry();
  const deleteEntry = useDeleteEntry();

  function toggleMore() {
    const next = !more;
    setMore(next);
    try { localStorage.setItem(MORE_KEY, next ? "1" : "0"); } catch { /* private mode */ }
  }

  const selectable = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");
  const project = projects.find((p) => p.id === projectId);
  // Billable / No charge / Internal, from the project unless picked (lib/billing).
  const hasClient = !!effectiveRate(project, clients).clientId;
  const kind = fitKind(kindPick ?? defaultKind(project, hasClient), project, hasClient);
  const taskGroups = useMemo(
    () => groupTasksByPhase(items, phases, projectId || undefined, itemProjectId),
    [items, phases, projectId],
  );
  const hasTasks = taskGroups.some((g) => g.tasks.length > 0);
  const task = items.find((i) => i.id === itemId);

  const today = toIsoLocal(new Date());
  const backdated = day !== today;
  /** What's set under More, named on its row while it's closed. The task
   *  already shows under the project, so it isn't repeated here. */
  const moreSummary = [
    backdated ? dayLabel(day) : "",
    kindPick ? KIND_LABEL[kind] : "",
    activity,
  ].filter(Boolean).join(" · ");
  const todayEntries = useMemo(
    () => entries.filter((e) => entryDayKey(e) === today)
      .sort((a, b) => (b.start || "").localeCompare(a.start || "")),
    [entries, today],
  );
  const todayTotals = totalsOf(todayEntries, projects);

  const week = useMemo(() => periodBounds("week", 0), []);
  const weekTotals = totalsOf(entries.filter((e) => inBounds(e, week)), projects);
  const days = useMemo(() => {
    const out: { key: string; seconds: number }[] = [];
    const d = new Date(week.start);
    for (let i = 0; i < 7; i++) {
      const key = toIsoLocal(d);
      out.push({ key, seconds: entries.filter((e) => entryDayKey(e) === key).reduce((n, e) => n + (e.durationSeconds || 0), 0) });
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, [entries, week]);
  const weekMax = Math.max(1, ...days.map((d) => d.seconds));

  useChrome({
    title: "Time",
    subtitle: `${new Date().toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · ${clock(todayTotals.seconds)} logged`,
  }, [todayTotals.seconds]);

  // Any text in the box means "custom", valid yet or not. Deciding from the
  // parse alone meant a half-typed "1:" (not a length yet) fell back to the
  // selected chip mid-keystroke: the chip lit up and the button offered its 30m.
  const hasCustom = custom.trim() !== "";
  const typed = hasCustom ? parseDuration(custom) : null;
  const customOk = typed !== null && typed > 0;
  const effective = hasCustom ? (customOk ? typed : 0) : minutes;

  function chooseActivity(next: string) {
    setActivity(next);
    const written = note.trim();
    if (!written || activities.includes(written)) setNote(next);
  }

  function resolve() {
    const r = effectiveRate(project, clients);
    return { ...r, ...kindFields(kind, r.rate) };
  }

  /**
   * Today's work ends NOW — that is what "I just did 30 minutes" means, and it
   * keeps the day's list in the order things happened. A past day has no "now"
   * to hang off, so entries stack from 9am in the order you add them rather
   * than piling onto the same minute.
   */
  function span(mins: number): { start: string; end: string } {
    if (!backdated) {
      const end = new Date();
      return { start: new Date(end.getTime() - mins * 60_000).toISOString(), end: end.toISOString() };
    }
    const already = entries
      .filter((e) => entryDayKey(e) === day)
      .reduce((n, e) => n + (e.durationSeconds || 0), 0);
    const [y, m, d] = day.split("-").map(Number);
    const start = new Date(y, m - 1, d, 9, 0, 0);
    start.setSeconds(start.getSeconds() + already);
    return { start: start.toISOString(), end: new Date(start.getTime() + mins * 60_000).toISOString() };
  }

  async function log() {
    if (hasCustom && !customOk) { toast("Try 1:30, 90 or 1.5h"); return; }
    setBusy(true);
    const { start, end } = span(effective);
    const r = resolve();
    try {
      await logEntry.mutateAsync({
        projectId: projectId || undefined,
        itemId: itemId || undefined,
        start, end,
        durationSeconds: effective * 60,
        note, category: activity || undefined,
        billable: r.billable, internal: r.internal, source: "manual",
        rate: r.rate, currency: r.currency, clientId: r.clientId,
      });
      setNote(""); setCustom("");
      toast(backdated ? `Logged ${durationLabel(effective)} to ${dayLabel(day)}` : `Logged ${durationLabel(effective)}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not save that entry");
    } finally {
      setBusy(false);
    }
  }

  function start() {
    const r = resolve();
    timer.start({
      projectId: projectId || undefined,
      itemId: itemId || undefined,
      clientId: r.clientId ?? undefined,
      category: activity || undefined,
      note, kind,
    });
    setNote("");
  }

  return (
    <div style={screen}>
      {timer.running ? <RunningCard /> : (
        <div style={{ ...card, padding: 14 }}>
          {/* The everyday path on the card itself: what it was, which project,
              a length, Log — or ▶ to time it instead. Task, kind, activity and
              date are under More; set there, they're named on the More row so
              a past date can't be logged by surprise. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void log(); } }}
              placeholder="What are you working on?"
              aria-label="What are you working on?"
              enterKeyHint="done"
              style={{ ...field, flex: 1, minWidth: 0, fontSize: 16, minHeight: 52 }}
            />
            <button
              onClick={start}
              disabled={backdated}
              aria-label="Start timer"
              title={backdated ? "A timer runs now — switch the date back to today" : "Start a timer"}
              style={{
                flex: "none", width: 52, height: 52, borderRadius: "50%", cursor: "pointer",
                border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
                opacity: backdated ? 0.4 : 1,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            ><PlayIcon size={20} /></button>
          </div>

          <div style={{ borderBottom: "1px solid var(--tint2)", marginTop: 6 }}>
            <PickerRow
              label="Project"
              value={projectId}
              onChange={(v) => { setProjectId(v); setItemId(""); setKindPick(null); }}
              options={[
                { value: "", label: "— No project" },
                ...selectable.map((p) => ({ value: p.id, label: p.client ? `${p.name} · ${p.client}` : p.name })),
              ]}
              color={project?.color}
              primary={project?.name}
              secondary={task?.title || project?.client}
              placeholder="Pick a project"
            />
          </div>

          <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
            {PRESETS.map((m) => {
              const on = !hasCustom && minutes === m;
              return (
                <button
                  key={m}
                  onClick={() => { setMinutes(m); setCustom(""); }}
                  className="mono"
                  style={{
                    flex: 1, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 14, padding: 0,
                    border: `1px solid ${on ? "var(--accentLine)" : "var(--line2)"}`,
                    background: on ? "var(--accentTint)" : "transparent",
                    color: on ? "var(--accent)" : "var(--muted)",
                  }}
                >{durationLabel(m)}</button>
              );
            })}
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="h:mm"
              aria-label="Custom length"
              className="mono"
              style={{
                flex: 1, minWidth: 0, minHeight: 44, borderRadius: 8, padding: "0 4px",
                textAlign: "center", fontSize: 14,
                border: `1px solid ${!hasCustom ? "var(--line2)" : customOk ? "var(--accentLine)" : "var(--warn)"}`,
                background: hasCustom ? "var(--accentTint)" : "transparent",
                color: hasCustom ? "var(--accent)" : "var(--ink)",
              }}
            />
          </div>

          <button onClick={() => void log()} disabled={busy || effective <= 0} style={{ ...tapPrimary, width: "100%", marginTop: 10, opacity: effective > 0 ? 1 : 0.55 }}>
            {effective > 0 ? `Log ${durationLabel(effective)}${backdated ? ` to ${dayLabel(day)}` : ""}` : "Finish the length…"}
          </button>

          <button
            onClick={() => toggleMore()}
            aria-expanded={more}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 44, marginTop: 10,
              padding: "0 2px", border: "none", borderTop: "1px solid var(--tint2)", background: "none",
              color: "var(--accent)", fontSize: 14, cursor: "pointer", textAlign: "left",
            }}
          >
            <span style={{ flex: "none" }}>{more ? "Less" : "More"}</span>
            <span style={{
              flex: 1, minWidth: 0, textAlign: "right", fontSize: 12.5,
              color: backdated ? "var(--accent)" : "var(--muted2)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{more ? "" : moreSummary || "Task, type, activity, date"}</span>
            <span aria-hidden style={{ flex: "none", color: "var(--muted2)", transform: more ? "rotate(180deg)" : undefined }}>▾</span>
          </button>

          {more && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
              {projectId && hasTasks && (
                <TaskPicker value={itemId} onChange={setItemId} groups={taskGroups} />
              )}

              <KindPicker value={kind} choices={kindChoices(project, hasClient)} onChange={setKindPick} size="lg" fromProject={!kindPick} />

              <div style={{ display: "flex", gap: 8 }}>
                {activities.length > 0 && (
                  <select
                    value={activity}
                    onChange={(e) => chooseActivity(e.target.value)}
                    aria-label="Activity"
                    style={{ ...field, flex: 1, minWidth: 0, color: activity ? "var(--ink)" : "var(--muted)" }}
                  >
                    <option value="">Activity…</option>
                    {activities.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                )}
                {/* Log to another day without leaving this screen. */}
                <input
                  type="date"
                  value={day}
                  max={today}
                  onChange={(e) => setDay(e.target.value || today)}
                  aria-label="Date"
                  style={{
                    ...field, flex: 1, minWidth: 0,
                    borderColor: backdated ? "var(--accentLine)" : "var(--line2)",
                    background: backdated ? "var(--accentTint)" : "var(--surface)",
                    color: backdated ? "var(--accent)" : "var(--ink)",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent meetings as one-tap entries; see Settings › Calendars. */}
      <CalendarCard entries={entries} projects={projects} compact />

      <SectionLabel right={`${todayEntries.length} · ${hours(todayTotals.seconds)} h`}>Logged today</SectionLabel>
      {todayEntries.length === 0 ? (
        <div style={{ padding: "4px 2px 8px", fontSize: 13.5, color: "var(--muted)" }}>
          Nothing logged yet today.
        </div>
      ) : (
        todayEntries.map((e) => (
          <EntryRow
            key={e.id}
            entry={e}
            color={projects.find((p) => p.id === e.project)?.color}
            label={deriveEntryLabel(e.note, items.find((i) => i.id === e.item)?.title)}
            project={projects.find((p) => p.id === e.project)?.name}
            kind={entryKind(e, projects)}
            onEdit={() => logSheet.open({ entry: e })}
            onContinue={() => void continueEntry(e)}
            onDelete={() => deleteEntry(e)}
          />
        ))
      )}

      <SectionLabel right={`${hours(weekTotals.seconds)} h`}>This week</SectionLabel>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 7 }}>
          {days.map((d, i) => {
            const isToday = d.key === today;
            const height = d.seconds > 0 ? Math.max(3, Math.round((d.seconds / weekMax) * 52)) : 2;
            return (
              <div key={d.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <div
                  title={`${d.key} — ${hours(d.seconds)} h`}
                  style={{
                    width: 20, height, borderRadius: 3,
                    background: isToday ? "var(--accent)" : d.seconds > 0 ? "var(--line2)" : "var(--line)",
                  }}
                />
                <span style={{ fontSize: 10, color: "var(--muted2)" }}>{DAY_LETTERS[i]}</span>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="mono" style={{ fontSize: 17, fontWeight: 600 }}>{hours(weekTotals.seconds)}h</div>
          <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
            {breakdownText(weekTotals, hours) || "nothing yet"}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The running timer replaces the logger rather than sitting beside it.
 *
 * Today's total deliberately does NOT move while this runs: a running timer is
 * not an entry until it stops. That is why the start time is on screen — it is
 * the only way to judge the pending amount before committing it.
 */
function RunningCard() {
  const timer = useTimer();
  const { openTimer } = useLogSheet();
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();
  const project = projects.find((p) => p.id === timer.meta.projectId);
  const task = items.find((i) => i.id === timer.meta.itemId);
  const [note, setNote] = useState(timer.meta.note ?? "");
  // Follow a description changed in the running sheet or on another device.
  useEffect(() => { setNote(timer.meta.note ?? ""); }, [timer.meta.note]);

  return (
    <div style={{
      ...card, padding: 14,
      border: "1px solid var(--accentLine)", background: "var(--accentTint2)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", color: "var(--accent)" }}>● RUNNING</span>
        <span className="mono" style={{ fontSize: 11.5, color: "var(--muted)" }}>
          since {timer.startedAt ? new Date(timer.startedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}
        </span>
      </div>
      <div className="mono" style={{ fontSize: 46, fontWeight: 500, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, margin: "8px 0 6px" }}>
        {formatElapsed(timer.seconds)}
      </div>
      {/* Started without a project? This is where you give it one — along with
          the task, the kind and the start time, in the running sheet. */}
      <button
        onClick={openTimer}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 40, padding: 0,
          border: "none", background: "none", cursor: "pointer", textAlign: "left", fontSize: 13.5, color: "var(--ink2)",
        }}
      >
        <span style={{ width: 9, height: 9, borderRadius: 3, background: project?.color ?? "var(--muted3)", flex: "none" }} />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project?.name ?? timer.meta.category ?? "No project"}
          {task ? <span style={{ color: "var(--muted)" }}> · {task.title}</span> : null}
        </span>
        <span style={{ flex: "none", fontSize: 12.5, color: "var(--accent)" }}>{project ? "Edit ›" : "Add project ›"}</span>
      </button>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if (note !== (timer.meta.note ?? "")) timer.update({ note }); }}
        placeholder="Add a note as you go…"
        aria-label="Note"
        style={{ ...field, marginTop: 6 }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={() => void timer.stop({ note })} style={{ ...tapPrimary, flex: 1 }}>Stop &amp; log</button>
        {/* Discarding by accident is expensive, so it gets a full target too. */}
        <button onClick={() => void timer.discard()} style={{ ...tapGhost, flex: "none", width: 104 }}>Discard</button>
      </div>
    </div>
  );
}

/** Delete an entry with the usual undo window, from a swipe. */
export function useDeleteEntry(): (entry: TimeEntry) => void {
  const undoable = useUndoableDelete();
  return (entry) => undoable({
    collection: "time_entries",
    id: entry.id,
    queryKeys: [["time_entries"]],
    message: "Entry deleted",
  });
}

/** "9:00 – 10:30 AM", dropping the first AM/PM when both ends share it. */
function timeSpan(entry: TimeEntry): string {
  const t = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (!entry.start) return "";
  if (!entry.end) return `${t(entry.start)} – running`;
  const a = t(entry.start), b = t(entry.end);
  const suffix = /\s?[AP]M$/i.exec(a)?.[0];
  return suffix && b.endsWith(suffix.trim()) ? `${a.slice(0, -suffix.length)} – ${b}` : `${a} – ${b}`;
}

export function EntryRow({ entry, label, project, color, kind, onEdit, onContinue, onDelete }: {
  entry: TimeEntry; label: string; project?: string; color?: string;
  /** Billable rows show only their times; any other kind says what it was. */
  kind: EntryKind;
  onEdit: () => void;
  /** ▶ on the row, and a swipe right: start a timer on the same work. */
  onContinue?: () => void;
  /** A swipe left. */
  onDelete?: () => void;
}) {
  const sub = [
    timeSpan(entry),
    kind === "hourly" ? "" : KIND_LABEL[kind].toLowerCase(),
    project || "No project",
    entry.invoicedAt ? `invoiced${entry.invoiceNumber ? ` ${entry.invoiceNumber}` : ""}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <SwipeActions
      radius={0}
      right={onContinue ? { label: "Continue", bg: "var(--goodTint)", ink: "var(--goodInk)", onFire: onContinue } : undefined}
      left={onDelete ? { label: "Delete", bg: "var(--badTint)", ink: "var(--bad)", onFire: onDelete } : undefined}
    >
      {/* Opaque, so the row covers the action strip until it is swiped. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, borderTop: "1px solid var(--tint2)", background: "var(--bg)" }}>
        <button
          onClick={onEdit}
          style={{
            display: "flex", alignItems: "flex-start", gap: 11, flex: 1, minWidth: 0, minHeight: 52,
            padding: "10px 2px", textAlign: "left", cursor: "pointer", border: "none", background: "none",
          }}
        >
          <span className="mono" style={{ flex: "none", width: 44, fontSize: 14, fontWeight: 500, color: "var(--ink)", marginTop: 1 }}>
            {clock(entry.durationSeconds)}
          </span>
          <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, marginTop: 5, background: color ?? "var(--muted3)" }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{
              display: "block", fontSize: 15, color: "var(--ink)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{label}</span>
            {/* When, and which job. "Weekly client check-in" is four different
                meetings until the project says which client. */}
            <span style={{
              display: "block", fontSize: 12, color: "var(--muted)", marginTop: 1,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{sub}</span>
          </span>
        </button>
        {onContinue && (
          <button
            onClick={onContinue}
            aria-label={`Continue “${label}”`}
            title="Start a timer on this again"
            style={{
              flex: "none", width: 36, height: 36, marginRight: 2, borderRadius: "50%", cursor: "pointer",
              border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          ><PlayIcon size={13} /></button>
        )}
      </div>
    </SwipeActions>
  );
}
